"use client";
import { DatePicker, TimeRangePickerProps } from "@basic-components";
import dayjs, { Dayjs } from "dayjs";
import { FC } from "react";

const OriginDateTimeRangePicker = DatePicker.RangePicker;

const getDate = (
  valueType: "string" | "array" | "long",
  splitChar: string,
  date?: any
) => {
  if (!date) return [];
  if (valueType === "array" || Array.isArray(date)) return date;
  return (date as string).split(splitChar);
};

interface DateTimeRangePickerProps
  extends Omit<TimeRangePickerProps, "value" | "onChange" | "onOk"> {
  value?: Array<string> | string | number;
  onChange?: (range: Array<string> | string) => void;
  onOk?: (dates: string | [string, string]) => void;
  valueType?: "string" | "array" | "long";
  /** 日期时间格式，默认 'YYYY-MM-DD HH:mm:ss' */
  format?: string;
  splitChar?: string;
  isView?: boolean;
}

export const DateTimeRangePicker: FC<DateTimeRangePickerProps> = (
  { value, format = "YYYY-MM-DD HH:mm:ss", onChange, valueType = "long", splitChar = ",", isView, onOk: externalOnOk, ...props }
) => {
  const dates = getDate(valueType, splitChar, value);

  const [formattedStartDate, formattedEndDate] = dates
    .filter((date: string | number) => date)
    .map((date: string | number) => {
      if (typeof date === "number") return dayjs(date);
      return dayjs(date, format as string);
    });

  const handleChange = (dates: any) => {
    const [formattedStartDate, formattedEndDate] = dates || [];
    const startDate =
      valueType === "long"
        ? formattedStartDate?.valueOf()
        : formattedStartDate?.format(format);
    const endDate =
      valueType === "long"
        ? formattedEndDate?.valueOf()
        : formattedEndDate?.format(format);
    onChange?.(
      ["array", "long"].includes(valueType)
        ? [startDate, endDate]
        : `${ startDate }${ splitChar }${ endDate }`
    );
  };

  if (isView)
    return dates.every((date: string) => date)
      ? valueType === "long"
        ? `${ dayjs(formattedStartDate).format(format) } ~ ${ dayjs(formattedEndDate).format(format) }`
        : `${ dates[0] } ~ ${ dates[1] }`
      : "--";

  const handleOk: (dates: [Dayjs | null, Dayjs | null] | null) => void = (dates) => {
    if (!externalOnOk) return;
    const [start, end] = dates ?? [];
    const startStr = start?.format(format) ?? "undefined";
    const endStr = end?.format(format) ?? "undefined";
    (externalOnOk as any)(
      ["array", "long"].includes(valueType) ? [startStr, endStr] : `${ startStr }${ splitChar }${ endStr }`
    );
  };

  return (
    <OriginDateTimeRangePicker
      {...props}
      showTime
      value={formattedStartDate ? [formattedStartDate, formattedEndDate] : null}
      onChange={handleChange}
      onOk={handleOk}
      format={format}
    />
  );
};
